import { doc, getDoc, setDoc, updateDoc } from "firebase/firestore";

/**
 * Matrix Structure Manager
 * 
 * This module handles the conversion between different matrix formats:
 * 1. 2D Array Format: Used in the application for processing
 *    Example: [ ["0,0", "1,0"], ["0,1", "1,1"] ]
 * 
 * 2. Firestore Object Format: Used for storage in Firestore
 *    Example: { "0_0": "0,0", "0_1": "1,0", "1_0": "0,1", "1_1": "1,1" }
 */

/**
 * Converts a 2D array matrix to Firestore-compatible format
 * @param {Array<Array<string>>} matrix - 2D array matrix
 * @returns {Object} - Firestore compatible object
 */
const convertMatrixToFirestore = (matrix) => {
    // Validate input
    if (!Array.isArray(matrix)) {
        console.error("Invalid matrix format: not an array");
        return {};
    }
    
    // Check for empty array
    if (matrix.length === 0) {
        console.error("Invalid matrix format: empty array");
        return {};
    }
    
    // Create an object with row_col keys
    const result = {};
    let hasEntries = false;
    
    for (let rowIndex = 0; rowIndex < matrix.length; rowIndex++) {
        const row = matrix[rowIndex];
        
        // Skip if row is not an array
        if (!Array.isArray(row)) {
            console.warn(`Row ${rowIndex} is not an array, skipping`);
            continue;
        }
        
        for (let colIndex = 0; colIndex < row.length; colIndex++) {
            const cell = row[colIndex];
            if (cell !== undefined && cell !== null) {
                result[`${rowIndex}_${colIndex}`] = cell;
                hasEntries = true;
            }
        }
    }
    
    // If no entries were added, something is wrong with the matrix
    if (!hasEntries) {
        console.error("No entries added to Firestore matrix format");
    }
    
    return result;
};

/**
 * Converts a Firestore format object back to a 2D array matrix
 * @param {Object} matrixObject - Firestore format matrix
 * @param {string} gridSize - Grid size in format "columns x rows"
 * @returns {Array<Array<string>>} - 2D array matrix
 */
export const convertFirestoreToMatrix = (matrixObject, gridSize) => {
    // Handle missing or invalid grid size with a fallback
    if (!gridSize || !gridSize.includes('x')) {
        console.log("Invalid grid size format:", gridSize);
        
        // Try to determine grid size from the matrix object itself if possible
        if (typeof matrixObject === 'object' && matrixObject !== null) {
            // For row-based format, check the number of rows and columns
            if (!Array.isArray(matrixObject) && Object.keys(matrixObject).every(key => !isNaN(parseInt(key)))) {
                const rows = Object.keys(matrixObject).length;
                const firstRow = matrixObject[Object.keys(matrixObject)[0]];
                const cols = firstRow && typeof firstRow === 'object' ? Object.keys(firstRow).length : 5;
                
                console.log(`Auto-detected grid size from matrix: ${cols}x${rows}`);
                gridSize = `${cols}x${rows}`;
            } 
            // For standard format with keys like "0_0", find the max row/col indices
            else if (!Array.isArray(matrixObject)) {
                const keys = Object.keys(matrixObject);
                if (keys.some(key => key.includes('_'))) {
                    let maxRow = 0, maxCol = 0;
                    
                    keys.forEach(key => {
                        const parts = key.split('_');
                        if (parts.length === 2) {
                            const [row, col] = parts.map(Number);
                            if (!isNaN(row) && !isNaN(col)) {
                                maxRow = Math.max(maxRow, row);
                                maxCol = Math.max(maxCol, col);
                            }
                        }
                    });
                    
                    // Grid size is max indices + 1
                    const rows = maxRow + 1;
                    const cols = maxCol + 1;
                    console.log(`Auto-detected grid size from matrix keys: ${cols}x${rows}`);
                    gridSize = `${cols}x${rows}`;
                }
            }
            
            // If we still don't have a valid grid size, use a default
            if (!gridSize || !gridSize.includes('x')) {
                gridSize = '5x5'; // Default to 5x5 grid
                console.log("Using default grid size 5x5");
            }
        } else {
            // Complete fallback
            gridSize = '5x5'; // Default to 5x5 grid
            console.log("Using default grid size 5x5");
        }
    }
    
    // Continue with the parsed dimensions
    const [columns, rows] = gridSize.split('x').map(Number);
    
    // Additional validation for parsed dimensions
    if (isNaN(columns) || isNaN(rows) || columns <= 0 || rows <= 0) {
        console.error("Invalid grid dimensions after parsing:", columns, rows);
        // Use a reasonable fallback
        const fallbackColumns = 5;
        const fallbackRows = 5;
        
        // Create a default matrix with the fallback dimensions
        const matrix = Array(fallbackRows).fill().map(() => Array(fallbackColumns).fill(''));
        
        // Add default values (coordinates)
        for (let r = 0; r < fallbackRows; r++) {
            for (let c = 0; c < fallbackColumns; c++) {
                matrix[r][c] = `${r},${c}`;
            }
        }
        
        return matrix;
    }
    
    // Create empty matrix with correct dimensions
    const matrix = Array(rows).fill().map(() => Array(columns).fill(''));
    
    // Handle matrix object based on its type
    if (matrixObject === null || matrixObject === undefined) {
        // Create a default matrix with coordinates as values
        for (let r = 0; r < rows; r++) {
            for (let c = 0; c < columns; c++) {
                matrix[r][c] = `${r},${c}`;
            }
        }
        return matrix;
    }
    
    // Check if matrixObject is already a 2D array
    if (Array.isArray(matrixObject)) {
        console.log("Matrix is already in array format, ensuring correct dimensions");
        
        // If nested in another array, unwrap it first
        const sourceMatrix = Array.isArray(matrixObject[0]) && matrixObject.length === 1 
            ? matrixObject[0] 
            : matrixObject;
            
        // Copy values to ensure correct dimensions
        for (let r = 0; r < Math.min(sourceMatrix.length, rows); r++) {
            const row = sourceMatrix[r];
            if (Array.isArray(row)) {
                for (let c = 0; c < Math.min(row.length, columns); c++) {
                    matrix[r][c] = row[c];
                }
            }
        }
        
        return matrix;
    }
    
    // Handle different Firestore format types
    if (typeof matrixObject === 'object' && matrixObject !== null) {
        // Check for row-based format (where keys are row indices)
        const hasRowFormat = Object.keys(matrixObject).every(key => !key.includes('_') && !isNaN(parseInt(key)));
        
        if (hasRowFormat) {
            // Handle row-based format
            Object.entries(matrixObject).forEach(([rowKey, rowValue]) => {
                const rowIndex = parseInt(rowKey, 10);
                
                if (rowIndex >= 0 && rowIndex < rows && typeof rowValue === 'object') {
                    Object.entries(rowValue).forEach(([colKey, cellValue]) => {
                        const colIndex = parseInt(colKey, 10);
                        
                        if (colIndex >= 0 && colIndex < columns) {
                            matrix[rowIndex][colIndex] = cellValue;
                        }
                    });
                }
            });
        } else {
            // Handle standard row_col format
            Object.entries(matrixObject).forEach(([key, value]) => {
                const parts = key.split('_');
                
                if (parts.length === 2) {
                    const [row, col] = parts.map(Number);
                    
                    if (!isNaN(row) && !isNaN(col) && row >= 0 && row < rows && col >= 0 && col < columns) {
                        matrix[row][col] = value;
                    }
                }
            });
        }
    }
    
    return matrix;
};

/**
 * Creates a default matrix with position values
 * @param {string} gridSize - Grid size in format "columns x rows"
 * @returns {Array<Array<string>>} - Default matrix
 */
const createDefaultMatrix = (gridSize) => {
    if (!gridSize || !gridSize.includes('x')) {
        console.error("Invalid grid size for default matrix:", gridSize);
        return [[]];
    }
    
    const [columns, rows] = gridSize.split('x').map(Number);
    
    return Array(rows).fill().map((_, rowIndex) => 
        Array(columns).fill().map((_, colIndex) => `${rowIndex},${colIndex}`)
    );
};

/**
 * Creates a default Firestore format matrix
 * @param {string} gridSize - Grid size in format "columns x rows"
 * @returns {Object} - Default Firestore format matrix
 */
const createDefaultFirestoreMatrix = (gridSize) => {
    if (!gridSize || !gridSize.includes('x')) {
        console.error("Invalid grid size for default Firestore matrix:", gridSize);
        return {};
    }
    
    const [columns, rows] = gridSize.split('x').map(Number);
    const result = {};
    
    for (let r = 0; r < rows; r++) {
        for (let c = 0; c < columns; c++) {
            result[`${r}_${c}`] = `${r},${c}`;
        }
    }
    
    return result;
};

/**
 * Saves session data to Firestore and localStorage
 */
export const saveSessionData = async (
    currentUser,
    db,
    uploadedImages,
    gridSize,
    quiltImageUrl,
    quiltGrid,
    generated,
    generateDisabled,
    aiGeneratedLayouts,
    quiltDimensions
) => {
    if (!currentUser || !currentUser.uid) return;

    const sessionRef = doc(db, 'sessions', currentUser.uid);

    // Process and validate AI layouts for Firestore
    const firestoreCompatibleLayouts = Array.isArray(aiGeneratedLayouts) 
        ? aiGeneratedLayouts.map(layout => {
            // Check for invalid layout object
            if (!layout || typeof layout !== 'object') {
                console.warn('Invalid layout object found, skipping');
                return null;
            }

            const layoutName = layout.layout_name || "Unnamed Layout";
            console.log(`Processing layout for Firestore: ${layoutName}`);
            
            // Get matrix data with proper validation
            let matrix = null;
            
            // Process matrix data based on its current format
            if (!layout.matrix) {
                console.warn(`Layout "${layoutName}" has no matrix, creating default`);
                matrix = createDefaultMatrix(gridSize);
            } 
            else if (Array.isArray(layout.matrix)) {
                // Handle nested array case (common with JSON parsing)
                if (layout.matrix.length === 1 && Array.isArray(layout.matrix[0])) {
                    console.log(`Unwrapping nested array for layout "${layoutName}"`);
                    matrix = layout.matrix[0];
                } else {
                    matrix = layout.matrix;
                }
                
                // Validate matrix has content
                const hasContent = matrix.some(row => 
                    Array.isArray(row) && row.some(cell => cell)
                );
                
                if (!hasContent) {
                    console.warn(`Layout "${layoutName}" has empty matrix, creating default`);
                    matrix = createDefaultMatrix(gridSize);
                }
            }
            else if (typeof layout.matrix === 'object') {
                // Check if already in Firestore format
                const keys = Object.keys(layout.matrix);
                const hasFirestoreFormat = keys.some(key => key.includes('_'));
                
                if (hasFirestoreFormat) {
                    console.log(`Layout "${layoutName}" already in Firestore format`);
                    return {
                        layout_name: layoutName,
                        description: layout.description || "",
                        matrix: layout.matrix,
                        original_grid_size: gridSize
                    };
                } else {
                    // Try to convert from row-based object format
                    try {
                        matrix = convertFirestoreToMatrix(layout.matrix, gridSize);
                    } catch (err) {
                        console.warn(`Failed to convert matrix for "${layoutName}", creating default`);
                        matrix = createDefaultMatrix(gridSize);
                    }
                }
            }
            
            // If we still don't have a valid matrix, create a default one
            if (!matrix || !Array.isArray(matrix) || matrix.length === 0) {
                console.warn(`Invalid matrix for "${layoutName}", creating default`);
                matrix = createDefaultMatrix(gridSize);
            }
            
            try {
                // Convert to Firestore format
                const firestoreMatrix = convertMatrixToFirestore(matrix);
                
                // Verify conversion produced valid results
                if (Object.keys(firestoreMatrix).length === 0) {
                    throw new Error("Matrix conversion resulted in empty object");
                }
                
                console.log(`Converted "${layoutName}" matrix to Firestore format with ${Object.keys(firestoreMatrix).length} entries`);
                
                return {
                    layout_name: layoutName,
                    description: layout.description || "",
                    matrix: firestoreMatrix,
                    original_grid_size: gridSize
                };
            } catch (error) {
                console.error(`Error converting matrix for "${layoutName}":`, error);
                
                // Create a fallback Firestore matrix
                const fallbackMatrix = createDefaultFirestoreMatrix(gridSize);
                
                return {
                    layout_name: layoutName,
                    description: layout.description || "",
                    matrix: fallbackMatrix,
                    original_grid_size: gridSize
                };
            }
        }).filter(Boolean) // Remove nulls
        : [];

    console.log(`Saving ${firestoreCompatibleLayouts.length} valid layouts to Firestore`);

    const sessionData = {
        uploadedImages,
        gridSize,
        quiltImageUrl,
        quiltGrid,
        generated,
        generateDisabled,
        aiGeneratedLayouts: firestoreCompatibleLayouts,
        layouts: firestoreCompatibleLayouts, // Legacy field for compatibility
        quiltDimensions,
        lastUpdated: new Date().toISOString()
    };

    try {
        await setDoc(sessionRef, sessionData, { merge: true });
        console.log("Session data saved to Firestore successfully");
        
        // Save to localStorage as backup
        try {
            localStorage.setItem('quiltBuilderState', JSON.stringify({
                uploadedImages,
                gridSize,
                quiltDimensions,
                quiltImageUrl,
                generated,
                generateDisabled,
                lastUpdated: new Date().toISOString()
            }));
        } catch (localStorageError) {
            console.warn("Could not save to localStorage:", localStorageError);
        }
    } catch (error) {
        console.error("Error saving session data to Firestore:", error);
    }
};

/**
 * Restores session data from Firestore or localStorage
 */
export const restoreSessionData = async (
    currentUser,
    db,
    setGridSize,
    setUploadedImages,
    setQuiltImageUrl,
    setQuiltGrid,
    setGenerated,
    setGenerateDisabled,
    setFeedbackSubmitted,
    setAIGeneratedLayouts,
    setQuiltDimensions,
    setRequiredImages,
    calculateTileCount,
    setLayoutSuggestions
) => {
    if (!currentUser) return {};
    
    try {
        const sessionRef = doc(db, 'sessions', currentUser.uid);
        const sessionSnap = await getDoc(sessionRef);
        
        if (sessionSnap.exists()) {
            const sessionData = sessionSnap.data();
            console.log("Restoring session data:", sessionData);
            
            // Restore grid size first
            if (sessionData.gridSize) {
                setGridSize(sessionData.gridSize);
                const calculatedImages = calculateTileCount(sessionData.gridSize);
                setRequiredImages(calculatedImages);
            }
            
            // Restore uploaded images from tile_list (NEW - this is the key fix)
            if (sessionData.tile_list && Array.isArray(sessionData.tile_list)) {
                console.log("Restoring uploaded images from tile_list:", sessionData.tile_list);
                
                const restoredImages = sessionData.tile_list.map((tile, index) => ({
                    url: tile.url,
                    tshirtUrl: tile.tshirt_url,
                    originalUrl: tile.tshirt_url, // Use tshirt_url as originalUrl
                    originalTshirtUrl: tile.tshirt_url,
                    dominantColor: tile.color,
                    processed: true, // Mark as processed since it's from Firestore
                    adjusted: tile.adjusted || false,
                    rotation: tile.rotation || 0,
                    crop: tile.crop_info || null,
                    tile_width: tile.tile_width || 300,
                    tile_height: tile.tile_height || 300,
                    temporary: tile.temporary || false,
                    // Add index for tracking
                    index: index
                }));
                
                console.log("Restored images from tile_list:", restoredImages);
                setUploadedImages(restoredImages);
            } else if (sessionData.uploadedImages && Array.isArray(sessionData.uploadedImages)) {
                // Fallback to old uploadedImages format
                console.log("Restoring uploaded images from uploadedImages:", sessionData.uploadedImages);
                setUploadedImages(sessionData.uploadedImages);
            }
            
            // Restore quilt image URL
            if (sessionData.quiltImageUrl) {
                setQuiltImageUrl(sessionData.quiltImageUrl);
            }
            
            // Restore quilt grid
            if (sessionData.quiltGrid && Array.isArray(sessionData.quiltGrid)) {
                setQuiltGrid(sessionData.quiltGrid);
            }
            
            // Restore other states
            if (sessionData.generated !== undefined) {
                setGenerated(sessionData.generated);
            }
            
            if (sessionData.generateDisabled !== undefined) {
                setGenerateDisabled(sessionData.generateDisabled);
            }
            
            if (sessionData.feedbackSubmitted !== undefined) {
                setFeedbackSubmitted(sessionData.feedbackSubmitted);
            }
            
            // Restore AI generated layouts
            if (sessionData.aiGeneratedLayouts && Array.isArray(sessionData.aiGeneratedLayouts)) {
                setAIGeneratedLayouts(sessionData.aiGeneratedLayouts);
            }
            
            // Restore quilt dimensions
            if (sessionData.quiltDimensions) {
                setQuiltDimensions(sessionData.quiltDimensions);
            }
            
            // Restore layout suggestions
            if (sessionData.layoutSuggestions && Array.isArray(sessionData.layoutSuggestions)) {
                setLayoutSuggestions(sessionData.layoutSuggestions);
            }
            
            return {
                gridSize: sessionData.gridSize,
                uploadedImages: sessionData.tile_list || sessionData.uploadedImages || [],
                quiltImageUrl: sessionData.quiltImageUrl,
                quiltGrid: sessionData.quiltGrid,
                generated: sessionData.generated,
                generateDisabled: sessionData.generateDisabled,
                aiGeneratedLayouts: sessionData.aiGeneratedLayouts,
                layoutSuggestions: sessionData.layoutSuggestions,
                quiltDimensions: sessionData.quiltDimensions
            };
        }
    } catch (error) {
        console.error("Error restoring session data:", error);
    }
    
    return {};
};

/**
 * Saves only the AI layouts part of the session data
 */
export const saveAILayouts = async (currentUser, db, aiGeneratedLayouts, gridSize) => {
    if (!currentUser || !currentUser.uid) return;
    
    try {
        const sessionRef = doc(db, 'sessions', currentUser.uid);
        
        // Process layouts with the same logic as in saveSessionData
        const firestoreCompatibleLayouts = aiGeneratedLayouts.map(layout => {
            const layoutName = layout.layout_name || "Unnamed Layout";
            let matrix = layout.matrix;
            
            // Handle nested array case
            if (Array.isArray(matrix) && matrix.length === 1 && Array.isArray(matrix[0])) {
                console.log(`Unwrapping nested array for layout "${layoutName}"`);
                matrix = matrix[0];
            }
            
            // Ensure matrix has correct dimensions
            if (Array.isArray(matrix)) {
                const [cols, rows] = gridSize.split('x').map(Number);
                
                // Check dimensions
                if (matrix.length < rows || (matrix[0] && matrix[0].length < cols)) {
                    // Create properly sized matrix
                    const expandedMatrix = Array(rows).fill().map(() => Array(cols).fill("0,0"));
                    
                    // Copy existing values
                    for (let r = 0; r < Math.min(matrix.length, rows); r++) {
                        if (Array.isArray(matrix[r])) {
                            for (let c = 0; c < Math.min(matrix[r].length, cols); c++) {
                                expandedMatrix[r][c] = matrix[r][c];
                            }
                        }
                    }
                    
                    matrix = expandedMatrix;
                }
            } else if (!matrix) {
                console.warn(`Layout "${layoutName}" has no matrix, creating default`);
                matrix = createDefaultMatrix(gridSize);
            }
            
            try {
                const firestoreMatrix = Array.isArray(matrix) 
                    ? convertMatrixToFirestore(matrix) 
                    : (typeof matrix === 'object' ? matrix : {});
                
                if (Object.keys(firestoreMatrix).length === 0) {
                    throw new Error("Empty matrix after conversion");
                }
                
                return {
                    layout_name: layoutName,
                    description: layout.description || "",
                    matrix: firestoreMatrix,
                    original_grid_size: gridSize
                };
            } catch (error) {
                console.error(`Error converting matrix for "${layoutName}":`, error);
                
                return {
                    layout_name: layoutName,
                    description: layout.description || "",
                    matrix: createDefaultFirestoreMatrix(gridSize),
                    original_grid_size: gridSize
                };
            }
        });
        
        await updateDoc(sessionRef, {
            aiGeneratedLayouts: firestoreCompatibleLayouts,
            layouts: firestoreCompatibleLayouts,
            lastUpdated: new Date().toISOString()
        });
        
        console.log(`AI layouts saved to Firestore with original grid size: ${gridSize}`);
    } catch (error) {
        console.error("Error saving AI layouts to Firestore:", error);
    }
};
